/* INTELEVO brand motion: ambient light, circuit pulses, book tilt, reveals and the hero sunrise. */
(() => {
  'use strict';
  const d = document, root = d.documentElement;
  const $ = (s, c = d) => c.querySelector(s);
  const $$ = (s, c = d) => Array.from(c.querySelectorAll(s));
  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  const mq = matchMedia('(prefers-reduced-motion: reduce)');
  const reduced = () => mq.matches;
  const finePointer = matchMedia('(hover: hover) and (pointer: fine)');

  /* header: frosted once the page scrolls */
  const onScroll = () => root.classList.toggle('iv-scrolled', scrollY > 24);
  addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  /* reveals */
  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } }), { rootMargin: '0px 0px -8% 0px', threshold: 0.08 })
    : null;
  const reveal = (el) => (io ? io.observe(el) : el.classList.add('is-in'));
  function initReveals(scope = d) {
    $$('[data-iv-reveal]', scope).forEach(reveal);
    $$('.ilc-card', scope).forEach((card, i) => {
      if (card.hasAttribute('data-iv-reveal') || card.getBoundingClientRect().top < innerHeight) return;
      card.setAttribute('data-iv-reveal', '');
      card.style.setProperty('--iv-d', ((i % 5) * 0.06).toFixed(2) + 's');
      reveal(card);
    });
  }

  /* circuit traces, drawn inline so small pulses of light run along them into the nodes */
  const CP = ['M0 40h80l20 20h90l14-14h60', 'M0 90h40l30 30h70', 'M0 150h110l25-25h50l12 12h48', 'M60 320v-60l20-20v-50l18-18', 'M130 320v-40l26-26h60l20-20h80', 'M0 220h70l18 18h40', 'M250 320v-30l14-14h70l20-20h66', 'M300 0v30l-16 16', 'M360 0v60l20 20h40'];
  const CN = [[264, 46, 3], [140, 120, 3], [233, 137, 3], [98, 172, 3], [128, 238, 3], [336, 234, 3], [284, 46, 2], [420, 80, 3]];
  function drawCircuits(scope = d) {
    $$('[data-iv-circuits]', scope).forEach((el, n) => {
      if (el.firstChild) return;
      const pulses = CP.map((p, i) => {
        const dur = 5 + ((i * 7 + n * 3) % 5) * 1.1, delay = -(((i * 13 + n * 5) % 11) * 0.73);
        return `<path d='${p}' pathLength='100' style='animation-duration:${dur.toFixed(1)}s;animation-delay:${delay.toFixed(2)}s'/>`;
      }).join('');
      const nodes = CN.map(([x, y, r], i) => `<circle cx='${x}' cy='${y}' r='${r}' style='animation-delay:${(-(i * 1.37) % 6).toFixed(2)}s'/>`).join('');
      el.innerHTML = `<svg viewBox='0 0 420 320' preserveAspectRatio='none' fill='none' stroke-linecap='round' stroke-linejoin='round' aria-hidden='true'><g class='iv-circ-base'>${CP.map((p) => `<path d='${p}'/>`).join('')}${CN.map(([x, y, r]) => `<circle cx='${x}' cy='${y}' r='${r}'/>`).join('')}</g><g class='iv-circ-pulse'>${pulses}</g><g class='iv-circ-nodes'>${nodes}</g></svg>`;
    });
  }

  /* ambient light: drifting embers, soft orbs and the odd rising streak. Each canvas runs only while on screen. */
  const pointer = { tx: 0.5, x: 0.5, active: false };
  const ambients = [];
  const AMB = { amber: '244,196,128', ember: '255,122,46', warm: '236,134,72', ice: '160,198,245' };
  const PRESETS = { hero: { motes: 1, orbs: 1, streaks: true }, soft: { motes: 0.45, orbs: 0.8, streaks: false }, closing: { motes: 0.45, orbs: 0.8, streaks: true } };
  function ambient(host) {
    if (host._ivAmbient) return;
    host._ivAmbient = true;
    const cfg = PRESETS[host.dataset.ivAmbient] || PRESETS.soft;
    const canvas = d.createElement('canvas');
    canvas.className = 'iv-ambient'; canvas.setAttribute('aria-hidden', 'true');
    host.prepend(canvas);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const R = Math.random;
    let w = 0, h = 0, motes = [], orbs = [], streaks = [], running = false, visible = false, nextStreak = 0;
    const spawnMote = (any) => ({ x: R() * w, y: any ? R() * h : h * (0.6 + R() * 0.4), r: R() * 1.4 + 0.35, v: R() * 0.3 + 0.06, a: R() * 0.5 + 0.15, ph: R() * 6.28, k: R() });
    const spawnOrb = (any) => ({ x: R() * w, y: any ? R() * h : h + 30, r: 1.3 + R() * 2.3, v: 0.05 + R() * 0.15, dx: (R() - 0.5) * 0.12, a: 0.32 + R() * 0.42, ph: R() * 6.28, c: R() < 0.62 ? AMB.ember : R() < 0.7 ? AMB.amber : AMB.ice });
    function size() {
      const dpr = Math.min(devicePixelRatio || 1, 2);
      w = canvas.clientWidth; h = canvas.clientHeight;
      if (!w || !h) return;
      canvas.width = w * dpr; canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      motes = Array.from({ length: Math.round(clamp((w * h) / 16000, 24, 80) * cfg.motes) }, () => spawnMote(true));
      orbs = Array.from({ length: Math.round(clamp((w * h) / 70000, 6, 18) * cfg.orbs) }, () => spawnOrb(true));
      streaks = [];
      if (reduced()) draw(0);
    }
    function draw(t) {
      ctx.clearRect(0, 0, w, h);
      const still = reduced();
      ctx.globalCompositeOperation = 'lighter';
      for (const p of motes) {
        if (!still) {
          p.y -= p.v; p.x += Math.sin(p.y / 70 + p.ph) * 0.18 + (pointer.x - 0.5) * 0.2 * p.r;
          if (p.y < -8 || p.x < -20 || p.x > w + 20) Object.assign(p, spawnMote(false));
        }
        const a = p.a * clamp(p.y / (h * 0.4)) * (0.65 + 0.35 * Math.sin(t / 700 + p.ph * 3));
        ctx.fillStyle = `rgba(${p.k < 0.55 ? AMB.amber : p.k < 0.8 ? AMB.warm : AMB.ice},${a})`;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, 6.2832); ctx.fill();
      }
      for (const o of orbs) {
        if (!still) {
          o.y -= o.v; o.x += o.dx + Math.sin(t / 2400 + o.ph) * 0.12;
          if (o.y < -30 || o.x < -30 || o.x > w + 30) Object.assign(o, spawnOrb(false));
        }
        const a = o.a * (0.55 + 0.45 * Math.sin(t / 1300 + o.ph * 2)) * clamp(o.y / (h * 0.25)) * clamp((h - o.y) / (h * 0.12) + 0.2);
        const g = ctx.createRadialGradient(o.x, o.y, 0, o.x, o.y, o.r * 6);
        g.addColorStop(0, `rgba(${o.c},${a})`); g.addColorStop(0.18, `rgba(${o.c},${a * 0.7})`); g.addColorStop(1, `rgba(${o.c},0)`);
        ctx.fillStyle = g; ctx.beginPath(); ctx.arc(o.x, o.y, o.r * 6, 0, 6.2832); ctx.fill();
      }
      if (cfg.streaks && !still) {
        if (t > nextStreak) {
          const side = R() < 0.5 ? 0.03 + R() * 0.24 : 0.73 + R() * 0.24;
          streaks.push({ x: w * side, y: h * (0.5 + R() * 0.45), len: 60 + R() * 80, v: 0.6 + R() * 0.7, life: 0, max: 200 + R() * 160, c: R() < 0.75 ? AMB.ember : AMB.ice });
          nextStreak = t + 1600 + R() * 2400;
        }
        streaks = streaks.filter((s) => s.life < s.max);
        ctx.lineWidth = 1.6; ctx.lineCap = 'round';
        for (const s of streaks) {
          s.life++; s.y -= s.v;
          const k = Math.sin((Math.PI * s.life) / s.max) * 0.6;
          const g = ctx.createLinearGradient(s.x, s.y, s.x, s.y + s.len);
          g.addColorStop(0, `rgba(${s.c},0)`); g.addColorStop(0.3, `rgba(${s.c},${k})`); g.addColorStop(1, `rgba(${s.c},0)`);
          ctx.strokeStyle = g; ctx.beginPath(); ctx.moveTo(s.x, s.y); ctx.lineTo(s.x, s.y + s.len); ctx.stroke();
        }
      }
      ctx.globalCompositeOperation = 'source-over';
    }
    function loop(t) { if (!running) return; draw(t); requestAnimationFrame(loop); }
    function update() {
      const should = visible && !d.hidden && !reduced();
      if (should && !running) { running = true; requestAnimationFrame(loop); } else if (!should) running = false;
    }
    if ('IntersectionObserver' in window) new IntersectionObserver(([e]) => { visible = e.isIntersecting; if (visible && canvas.clientWidth !== w) size(); update(); }).observe(host);
    ambients.push({ size, update });
  }
  d.addEventListener('visibilitychange', () => ambients.forEach((a) => a.update()));
  let rz;
  addEventListener('resize', () => { clearTimeout(rz); rz = setTimeout(() => { ambients.forEach((a) => a.size()); $$('[data-iv-hero]').forEach(layoutSunrise); }, 120); });
  if (mq.addEventListener) mq.addEventListener('change', () => ambients.forEach((a) => a.update()));

  /* gentle 3D tilt and glare on covers */
  let tiltEl = null;
  const tiltTarget = (host) => (host.classList.contains('ilc-card__link') ? $('.ilc-card__media', host) : $('.iv-book-3d', host) || host);
  const resetTilt = (host) => { const t = tiltTarget(host); if (t) ['--rx', '--ry'].forEach((p) => t.style.removeProperty(p)); };
  d.addEventListener('pointermove', (e) => {
    if (e.pointerType !== 'mouse' || reduced() || !finePointer.matches) return;
    const host = e.target.closest ? e.target.closest('[data-iv-tilt], .ilc-card__link') : null;
    if (host !== tiltEl) { if (tiltEl) resetTilt(tiltEl); tiltEl = host; }
    if (!host) return;
    const t = tiltTarget(host); if (!t) return;
    const r = t.getBoundingClientRect();
    const x = clamp((e.clientX - r.left) / r.width), y = clamp((e.clientY - r.top) / r.height);
    t.style.setProperty('--ry', ((x - 0.5) * 16).toFixed(2) + 'deg');
    t.style.setProperty('--rx', ((0.5 - y) * 12).toFixed(2) + 'deg');
    t.style.setProperty('--gx', (x * 100).toFixed(1) + '%');
    t.style.setProperty('--gy', (y * 100).toFixed(1) + '%');
  }, { passive: true });
  d.addEventListener('mouseout', (e) => { if (!e.relatedTarget && tiltEl) { resetTilt(tiltEl); tiltEl = null; } });

  /* hero: a sunrise of covers fanned on an arc */
  function layoutSunrise(hero) {
    const sr = $('[data-iv-sunrise]', hero), cta = $('.iv-cta-row', hero) || $('.iv-hero__logo', hero);
    if (!sr || !cta) return;
    const rays = $$('.iv-ray', sr), mobile = innerWidth < 720;
    const n = Math.min(rays.length, mobile ? 5 : innerWidth < 1100 ? 7 : 9);
    const first = Math.floor((rays.length - n) / 2);
    const top = cta.getBoundingClientRect().bottom - hero.getBoundingClientRect().top;
    const room = Math.max(120, hero.offsetHeight - top - (mobile ? 26 : 40));
    const rw = Math.round(clamp((room / 0.9) * 0.7727, mobile ? 84 : 118, mobile ? 118 : 196));
    const Hc = rw / 0.7727, visible = Math.min(room, Hc * 0.92);
    const R = Math.round(rw * (mobile ? 3.1 : 4.3)), step = mobile ? 15 : n === 9 ? 7.2 : 8.6;
    sr.style.setProperty('--rays-drop', `${-(R + Hc - visible)}px`);
    rays.forEach((el, k) => {
      const i = k - first, on = i >= 0 && i < n;
      el.hidden = !on;
      if (!on) return;
      const off = i - (n - 1) / 2;
      el.style.cssText = `--rw:${rw}px;--R:${R}px;--a:${(off * step).toFixed(2)}deg;--z:${20 - Math.abs(off)};--d:${(0.1 + Math.abs(off) * 0.09).toFixed(2)}s`;
    });
    sr.classList.add('is-laid');
  }
  function initHero(hero) {
    if (hero._ivHero) return;
    hero._ivHero = true;
    const sr = $('[data-iv-sunrise]', hero);
    let started = false;
    const go = () => {
      if (started) return;
      started = true;
      layoutSunrise(hero);
      if (!sr) return;
      sr.classList.add('is-entering');
      requestAnimationFrame(() => requestAnimationFrame(() => hero.classList.remove('iv-pre')));
      setTimeout(() => sr.classList.remove('is-entering'), 2600);
    };
    const img = $('.iv-hero__img', hero);
    if (img && !img.complete) { img.addEventListener('load', go, { once: true }); img.addEventListener('error', go, { once: true }); setTimeout(go, 1200); } else go();
    hero.addEventListener('pointermove', (e) => {
      pointer.tx = e.clientX / innerWidth;
      if (pointer.active || reduced()) return;
      pointer.active = true;
      const step = () => {
        pointer.x += (pointer.tx - pointer.x) * 0.06;
        if (sr) sr.style.setProperty('--sway', ((pointer.x - 0.5) * 3).toFixed(2) + 'deg');
        if (Math.abs(pointer.tx - pointer.x) > 0.001) requestAnimationFrame(step); else pointer.active = false;
      };
      requestAnimationFrame(step);
    }, { passive: true });
  }

  /* page headers on other templates share the website's light: embers on collections and pages, circuits on product pages */
  function decoratePages() {
    const main = $('main[data-template]');
    if (!main || main._ivDeco) return;
    main._ivDeco = true;
    const tpl = main.dataset.template || '';
    let host = null, embers = true;
    if (/^(collection|list-collections|search|page|blog|article|404)/.test(tpl)) host = main.querySelector(':scope > .shopify-section');
    else if (/^product/.test(tpl)) { host = $('.product-information', main); embers = false; }
    if (!host || host.querySelector('.iv-circuits')) return;
    host.classList.add('iv-deco');
    host.insertAdjacentHTML('afterbegin', `<div class='iv-circuits tr' data-iv-circuits aria-hidden='true'></div>`);
    if (embers) host.dataset.ivAmbient = 'soft';
  }

  function init(scope = d) {
    decoratePages();
    drawCircuits(scope);
    $$('[data-iv-ambient]', scope).forEach(ambient);
    $$('[data-iv-hero]', scope).forEach(initHero);
    initReveals(scope);
  }
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', () => init()); else init();
  d.addEventListener('shopify:section:load', (e) => init(e.target));
})();